/** /language_aliases/ — cross-tenant Language_Alias catalog (list + CRUD).
 *
 * A language alias maps an arbitrary value a policy may return (matched
 * case-insensitively, stored lowercased) onto a target ISO 639-1 language code.
 * `tour_operator` is nullable: a null tenant is a system-wide alias usable by
 * every operator.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). No optimistic-concurrency token — writes are
 * last-writer-wins.
 */
import { runBatch, type BatchResponse, type BatchSelection } from './batch';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

export interface Alias {
  id: number;
  /** Owning tenant. Null for system-wide aliases. */
  tenant: TenantRef | null;
  /** The value a policy may return (matched case-insensitively, stored lowercased). */
  alias: string;
  /** Target ISO 639-1 code, e.g. 'en'. */
  language_code: string;
  enabled: boolean;
  created_ts: string | null;
  updated_ts: string | null;
}

/** Create/update body. `alias` + `language_code` required on create;
 *  `tour_operator` null = system-wide. */
export interface AliasWrite {
  tour_operator?: number | null;
  alias?: string;
  language_code?: string;
  enabled?: boolean;
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  /** alias + language_code (icontains). */
  search?: string;
  /** One id or a comma list of ids (D1), or 'system' / 'null' for system-wide aliases (no tenant). */
  tour_operator_id?: TenantIdsParam;
  enabled?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) {
    usp.set('tour_operator_id', String(params.tour_operator_id));
  }
  if (params.enabled !== undefined) usp.set('enabled', String(params.enabled));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<Alias>> {
  return apiGet<PaginatedResponse<Alias>>(`/language_aliases/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<Alias> {
  return apiGet<Alias>(`/language_aliases/${id}/`);
}

export function create(body: AliasWrite): Promise<Alias> {
  return apiPost<Alias>('/language_aliases/', body);
}

export function update(id: number, body: AliasWrite): Promise<Alias> {
  return apiPatch<Alias>(`/language_aliases/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/language_aliases/${id}/`);
}

/** Generic `/language_aliases/batch/` delete dispatch (background task). */
export function batchDelete(
  selection: BatchSelection,
  filterQuery?: string,
): Promise<BatchResponse> {
  return runBatch('language_aliases', 'delete', selection, undefined, filterQuery);
}
